import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useRowValidation } from "./useRowValidation";
import { ValidationRule } from "../../types/types.validation";
import { ToastService } from "../../service/toast.service";

interface ValidationError {
  field: string;
  message: string;
}

export interface PendingRow {
  id: string;
  data: Record<string, any>;
  errors?: Record<string, string>;
  isValidating?: boolean;
  isSaving?: boolean;
}

interface UsePendingRowsProps {
  fields: Array<{
    name: string;
    file_type?: string;
    type?: string;
    validation?: ValidationRule[];
  }>;
  moduleId?: string | number;
  autoSaveEnabled?: boolean;
  validateRow?: (data: Record<string, any>) => Promise<ValidationError[]>;
  createData?: (params: Record<string, any>) => Promise<any>;
  autoFillFields?: (
    data: Record<string, any>,
    changedField: string,
  ) => Promise<Record<string, any>>;
  loadData?: () => Promise<void>;
  t?: (key: string) => string;
}

export function usePendingRows(props: UsePendingRowsProps) {
  const [pendingRows, setPendingRows] = useState<PendingRow[]>([]);
  const saveTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const cellRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  const {
    validateRow: validateRowFrontend,
    transformValue,
    isRowEmpty,
  } = useRowValidation({
    fields: props.fields,
  });

  useLayoutEffect(() => {
    if (pendingRows.length > 0) {
      requestAnimationFrame(() => {
        if (firstFieldRef.current) {
          firstFieldRef.current.focus();
          if (
            "select" in firstFieldRef.current &&
            firstFieldRef.current instanceof HTMLInputElement
          ) {
            firstFieldRef.current.select();
          }
        }
      });
    }
  }, [pendingRows.length]);

  useEffect(() => {
    if (!props.moduleId) return;

    const saved = localStorage.getItem(`pendingRows_${props.moduleId}`);
    if (saved) {
      try {
        setPendingRows(JSON.parse(saved));
      } catch {}
    }
  }, [props.moduleId]);

  useEffect(() => {
    if (!props.moduleId) return;

    if (pendingRows.length > 0) {
      localStorage.setItem(
        `pendingRows_${props.moduleId}`,
        JSON.stringify(pendingRows),
      );
    } else {
      localStorage.removeItem(`pendingRows_${props.moduleId}`);
    }
  }, [pendingRows, props.moduleId]);

  const addNewRow = () => {
    const newId = `temp_${Date.now()}`;
    const emptyData: Record<string, any> = {};

    props.fields.forEach((col) => {
      const fieldType = col.file_type || col.type;

      if (fieldType === "boolean" || fieldType === "bool") {
        emptyData[col.name] = false;
      } else if (fieldType === "date") {
        emptyData[col.name] = new Date().toISOString().split("T")[0];
      } else if (fieldType === "datetime" || fieldType === "timestamp") {
        emptyData[col.name] = new Date().toISOString().slice(0, 16);
      } else if (fieldType === "color") {
        emptyData[col.name] = "#000000";
      } else if (fieldType === "json") {
        emptyData[col.name] = "{}";
      } else {
        emptyData[col.name] = "";
      }
    });

    setPendingRows((prev) => [
      ...prev,
      {
        id: newId,
        data: emptyData,
        errors: {},
        isValidating: false,
        isSaving: false,
      },
    ]);
  };

  const validateRowInternal = async (rowData: Record<string, any>) => {
    const frontendResult = validateRowFrontend(rowData);

    if (!frontendResult.isValid) {
      return frontendResult.errors;
    }

    if (props.validateRow) {
      const backendErrors = await props.validateRow(rowData);
      const errorsObj: Record<string, string> = {};
      backendErrors.forEach((err) => {
        errorsObj[err.field] = err.message;
      });
      return errorsObj;
    }

    return {};
  };

  const handleCellChange = async (rowId: string, field: string, value: any) => {
    const transformedValue = transformValue(field, value);
    const row = pendingRows.find((r) => r.id === rowId);

    if (!row) return;

    const updatedData = { ...row.data, [field]: transformedValue };

    setPendingRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, data: updatedData } : r)),
    );

    if (props.autoFillFields) {
      try {
        const autoFilledData = await props.autoFillFields(updatedData, field);
        const finalData = { ...updatedData, ...autoFilledData };
        const errors = await validateRowInternal(finalData);

        setPendingRows((prev) =>
          prev.map((r) =>
            r.id === rowId ? { ...r, data: finalData, errors } : r,
          ),
        );
      } catch {
        const errors = await validateRowInternal(updatedData);
        setPendingRows((prev) =>
          prev.map((r) => (r.id === rowId ? { ...r, errors } : r)),
        );
      }
    } else {
      const errors = await validateRowInternal(updatedData);
      setPendingRows((prev) =>
        prev.map((r) => (r.id === rowId ? { ...r, errors } : r)),
      );
    }

    if (props.autoSaveEnabled) {
      const timeout = saveTimeouts.current.get(rowId);
      if (timeout) clearTimeout(timeout);

      const newTimeout = setTimeout(() => {
        saveRow(rowId);
      }, 800);

      saveTimeouts.current.set(rowId, newTimeout);
    }
  };

  const saveRow = async (rowId: string) => {
    const row = pendingRows.find((r) => r.id === rowId);
    if (!row || !props.createData) return;

    if (isRowEmpty(row.data)) {
      return;
    }

    setPendingRows((prev) =>
      prev.map((r) =>
        r.id === rowId ? { ...r, isSaving: true, errors: {} } : r,
      ),
    );

    const errors = await validateRowInternal(row.data);

    if (Object.keys(errors).length > 0) {
      setPendingRows((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? { ...r, errors, isSaving: false, isValidating: false }
            : r,
        ),
      );
      return;
    }

    try {
      const clean: Record<string, any> = {};

      Object.entries(row.data).forEach(([k, v]) => {
        if (k === "id") return;

        const field = props.fields.find((f) => f.name === k);

        if (typeof v === "boolean") {
          clean[k] = v;
          return;
        }

        if (v === "" || v === null || v === undefined) {
          return;
        }

        if (field) {
          const fileType = field.file_type || field.type;

          if (fileType === "json") {
            if (typeof v === "object") {
              clean[k] = JSON.stringify(v);
            } else if (typeof v === "string") {
              clean[k] = v;
            }
            return;
          }

          const isTextArea = fileType === "text" || fileType === "textarea";
          let processedValue = v;

          if (typeof v === "string" && !isTextArea) {
            processedValue = v.trim();
          }

          if (
            fileType === "int" ||
            fileType === "integer" ||
            fileType === "INT8"
          ) {
            const num = parseInt(String(processedValue).trim(), 10);
            if (!isNaN(num)) {
              clean[k] = num;
              return;
            }
          }

          if (
            fileType === "double" ||
            fileType === "float" ||
            fileType === "decimal"
          ) {
            const num = parseFloat(
              String(processedValue).trim().replace(",", "."),
            );
            if (!isNaN(num)) {
              clean[k] = num;
              return;
            }
          }

          clean[k] = processedValue;
        } else {
          clean[k] = typeof v === "string" ? v.trim() : v;
        }
      });

      await props.createData(clean);

      ToastService.success(props.t?.("rowAdded") || "Row created successfully");

      setPendingRows((prev) => prev.filter((r) => r.id !== rowId));

      await props.loadData?.();

      if (props.autoSaveEnabled) {
        addNewRow();
      } else {
        setTimeout(() => {
          const firstRow = document.querySelector('[data-row-index="0"]');
          if (firstRow) {
            const firstInput = firstRow.querySelector(
              'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), button:not([disabled])',
            ) as HTMLElement;
            firstInput?.focus();
          }
        }, 100);
      }
    } catch (err: any) {
      const errorMessage = err.message || "Ошибка сохранения";
      ToastService.error(errorMessage);

      setPendingRows((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? {
                ...r,
                isSaving: false,
                errors: { _general: errorMessage },
              }
            : r,
        ),
      );
    }
  };

  const saveAllPendingRows = async () => {
    for (const row of pendingRows) {
      if (!isRowEmpty(row.data)) {
        await saveRow(row.id);
      }
    }
  };

  return {
    pendingRows,
    setPendingRows,
    addNewRow,
    handleCellChange,
    saveRow,
    saveAllPendingRows,
    isRowEmpty,
    cellRefs,
    firstFieldRef,
  };
}
